import { z } from "zod/v4";
import { DateUtils } from "../../../primitive-utils";
import { YEAR_MONTH_FORMAT } from "../../../time";
import { REGEX_LOCAL_YEAR_MONTH } from "../base/time-regex";
import { MonthSchema, YearSchema } from "../base/time-schema";
const YearMonthSchema = z
    .object({
    ...YearSchema.shape,
    ...MonthSchema.shape,
})
    .refine(({ year, month }) => {
    const date = new Date(year, month - 1);
    return date.getFullYear() === year && date.getMonth() + 1 === month;
}, {
    error: "存在しない日付です。",
})
    .brand("Year")
    .brand("Month");
// ** YearMonth <-> Date 変換系 ** //
/** DateをJST解釈のYearMonthに変換 */
const fromDateJST = (v) => {
    const jstDate = DateUtils.utcToJSTTime(v);
    return fromDate(jstDate);
};
/** YearMonthをJST解釈のDateに変換 */
const startOfMonthJST = (v) => {
    return DateUtils.startOfDayJST(toDate(v));
};
/** YearMonthをJST解釈のDateに変換 */
const endOfMonthJST = (v) => {
    return DateUtils.endOfMonthJST(toDate(v));
};
/** private. TZは実行環境に依存する */
const toDate = ({ year, month }) => {
    return new Date(year, month - 1);
};
/** private. TZは実行環境に依存する */
const fromDate = (d) => {
    return YearMonth.of({
        year: d.getFullYear(),
        month: d.getMonth() + 1,
    });
};
// ** YearMonth <-> string 変換系 ** //
/** YearMonth -> string */
const format = (v, format = YEAR_MONTH_FORMAT) => {
    return DateUtils.format(toDate(v), format, true);
};
/**
 * 2020-01, 2020/01, 2020年01月 などの形式のYearMonthを扱うえるようにするスキーマ
 */
const YearMonthStringSchema = z
    .string()
    .regex(REGEX_LOCAL_YEAR_MONTH, { message: "年月の形式が不正です。" })
    .transform((val) => {
    const extractRegex = /\d+/g; // 数字を抽出
    const [year, month] = val.match(extractRegex)?.map(Number) ?? [];
    return { year, month };
});
/** 形式チェックのみで、厳密な日付存在チェックを経ないparse */
const parseSoft = (v) => {
    return YearMonthStringSchema.parse(v);
};
/** string => YearMonth */
const parse = (v) => {
    return YearMonth.schema.parse(parseSoft(v));
};
// ** YearMonthの判定系 ** //
// USAGE: localDateArray.sort(LocalDate.compere);
// 日の昇順 1/1, 1/2, 1/3... で並ぶ
const compare = (a, b) => {
    return toDate(a).getTime() - toDate(b).getTime();
};
// 日の降順 1/3,1/2,1/1... で並ぶ
const compareDesc = (a, b) => {
    return toDate(b).getTime() - toDate(a).getTime();
};
const equals = (a, b) => {
    return toDate(a).getTime() === toDate(b).getTime();
};
const isAfter = (a, b) => {
    return compare(a, b) > 0;
};
const isEqualOrAfter = (a, b) => {
    return compare(a, b) >= 0;
};
const isBefore = (a, b) => {
    return compare(a, b) < 0;
};
const isEqualOrBefore = (a, b) => {
    return compare(a, b) <= 0;
};
// ** YearMonthの操作系 ** //
const addYears = (v, years) => {
    return fromDate(DateUtils.addYears(toDate(v), years));
};
const subYears = (v, years) => {
    return fromDate(DateUtils.subYears(toDate(v), years));
};
const addMonths = (v, months) => {
    return fromDate(DateUtils.addMonths(toDate(v), months));
};
const subMonths = (v, months) => {
    return fromDate(DateUtils.subMonths(toDate(v), months));
};
const min = (...arr) => {
    return arr.sort(compare)[0];
};
const max = (...arr) => {
    return arr.sort(compareDesc)[0];
};
//** a, b間のYearMonthをリストで返す */
const toArray = (a, b) => {
    const array = [];
    const before = min(a, b);
    const after = max(a, b);
    for (let yearMonth = before; isEqualOrBefore(yearMonth, after); yearMonth = addMonths(yearMonth, 1)) {
        array.push(yearMonth);
    }
    return array;
};
export const YearMonth = {
    schema: YearMonthSchema,
    stringSchema: YearMonthStringSchema,
    THIS_MONTH: () => fromDateJST(new Date()),
    of(args) {
        return YearMonth.schema.parse(args);
    },
    // ** YearMonth <-> Date 変換系 ** //
    fromDateJST,
    startOfMonthJST,
    endOfMonthJST,
    // ** YearMonth <-> string 変換系 ** //
    format,
    parseSoft,
    parse,
    // ** YearMonthの判定系 ** //
    compare,
    compareDesc,
    equals,
    isAfter,
    isEqualOrAfter,
    isBefore,
    isEqualOrBefore,
    // ** YearMonthの操作系 **  //
    addYears,
    subYears,
    addMonths,
    subMonths,
    min,
    max,
    toArray,
};
//# sourceMappingURL=year-month.js.map